import { useMemo, useState } from "react";

import { resolveGanttLayout } from "@/ds";
import { useMediaQuery } from "@/hooks";
import type { EquipeObraTarget } from "@/modules/recursos";
import { conflitoLabel } from "@/modules/carga-trabalho";
import { SEM_MARCAS } from "@/modules/recursos/simulacao";

import { lineWidthRem, OBRAS_LAYOUT } from "../../gantt-obras-layout";
import type { ObraGroup } from "@/shared/components/gantt-carga/gantt-obras-rows";
import { ganttScaleOf, monthMinWidth } from "@/shared/components/gantt-carga/gantt-scale";
import { CARGA_TRABALHO_MOBILE_QUERY } from "@/shared/components/gantt-carga/mobile";
import { DESKTOP_COLUMNS, MOBILE_COLUMNS } from "./gantt-obras.columns";
import type { GanttObrasProps, GanttObrasViewModel } from "./types-gantt-obras";

/**
 * Gantt da visão por Obras (Figma: Carga de Trabalho / Obra). No desktop, colunas fixas de
 * Obra e Profissionais; no mobile, uma coluna só e a obra expande em linhas de profissional.
 */
export function useGanttObras({
  groups,
  timeline,
  today,
  onDefinirEquipe,
  onEditarEquipe,
  marcas = SEM_MARCAS,
}: GanttObrasProps): GanttObrasViewModel {
  const isMobile = useMediaQuery(CARGA_TRABALHO_MOBILE_QUERY);
  const scale = useMemo(() => ganttScaleOf(timeline), [timeline]);
  const { monthWidthRem, monthWidthMobileRem } = resolveGanttLayout(OBRAS_LAYOUT);
  const [aberto, setAberto] = useState<Set<string>>(new Set());

  function acoesDaObra(group: ObraGroup) {
    const { obraInicio, obraFim } = group;
    if (!obraInicio || !obraFim) return undefined;

    const target: EquipeObraTarget = {
      id: group.obraId,
      codigo: group.codigo,
      nome: group.nome,
      embarcacao: { nome: group.embarcacao, acronimo: group.embarcacaoSigla },
      inicio: obraInicio,
      fim: obraFim,
    };

    if (group.semEquipe) {
      if (!onDefinirEquipe) return undefined;
      return [
        { label: "Definir Equipe", icon: "pi pi-users", command: () => onDefinirEquipe(target) },
      ];
    }

    if (!onEditarEquipe) return undefined;
    return [
      { label: "Editar Equipe", icon: "pi pi-pencil", command: () => onEditarEquipe(target) },
    ];
  }

  return {
    groups,
    today,
    marcas,
    rotuloConflito: conflitoLabel,
    scale,
    isMobile,
    unitMinWidth: monthMinWidth(OBRAS_LAYOUT, isMobile),
    colunas: isMobile ? MOBILE_COLUMNS : DESKTOP_COLUMNS,
    larguraDaLinha: (width) =>
      lineWidthRem(width, timeline.months.length, isMobile ? monthWidthMobileRem : monthWidthRem),
    expandida: (key) => aberto.has(key),
    alternar: (key) =>
      setAberto((atual) => {
        const proximo = new Set(atual);
        if (proximo.has(key)) proximo.delete(key);
        else proximo.add(key);
        return proximo;
      }),
    acoesDaObra,
    semAcoes: !onDefinirEquipe && !onEditarEquipe,
  };
}
